
import React, {
    useEffect,
    useRef,
    useState
} from "react";

import { useTranslation } from "react-i18next";

import {
    postForumReply,
    updateForumReply,
    uploadFileToForumReply
} from "../../services/forumReplyService";

import {
    getLoggedInUser
} from "../../pages/userapp/ServiceUserApp";


// ======================================================
// CONSTANTES
// ======================================================

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const ALLOWED_EXTENSIONS = [
    ".pdf",
    ".jpg",
    ".jpeg",
    ".png",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".ppt",
    ".pptx",
    ".mp4",
    ".webm"
];


// ======================================================
// COMPOSANT
// ======================================================

export default function ForumReplyComposer({
    topic,
    reply,
    onClose,
    onSaved
}) {

    const { t } = useTranslation();


    // ==================================================
    // FORMAT DATE
    // ==================================================

    const formatDateTime = (dateValue) => {

        if (!dateValue) {
            return "";
        }

        return new Intl.DateTimeFormat("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
        }).format(new Date(dateValue));
    };


    // ==================================================
    // UTILISATEUR CONNECTÉ
    // ==================================================

    const connectedUser = getLoggedInUser();


    // ==================================================
    // ETAT INITIAL
    // ==================================================

    const emptyReply = {
        id: null,

        forumTopic: topic,

        sender: connectedUser,

        messageObject: topic?.topicObject || "",

        messageBody: "",

        attachedFile1: null,
        attachedFile2: null,
        attachedFile3: null,
        attachedFile4: null
    };


    // ==================================================
    // STATES
    // ==================================================

    const [data, setData] = useState(emptyReply);

    const [loading, setLoading] = useState(false);

    const [errors, setErrors] = useState({});


    // ==================================================
    // FICHIERS
    // ==================================================

    const [files, setFiles] = useState({
        file1: null,
        file2: null,
        file3: null,
        file4: null
    });


    // ==================================================
    // REFERENCES DES INPUT FILE
    // ==================================================

    const fileInputRefs = useRef([]);


    // ==================================================
    // VALIDATION FICHIER
    // ==================================================

    const validateFile = (file) => {

        if (!file) {
            return null;
        }


        // ----------------------------------------------
        // Taille
        // ----------------------------------------------

        if (file.size > MAX_FILE_SIZE) {

            return (
                `Le fichier "${file.name}" dépasse ` +
                `la taille maximale autorisée de 20 Mo.`
            );
        }


        // ----------------------------------------------
        // Extension
        // ----------------------------------------------

        const extension =
            file.name
                .substring(
                    file.name.lastIndexOf(".")
                )
                .toLowerCase();


        if (!ALLOWED_EXTENSIONS.includes(extension)) {

            return (
                `Le type de fichier "${extension}" ` +
                `n'est pas autorisé.`
            );
        }


        return null;
    };


    // ==================================================
    // CHARGEMENT / MODIFICATION
    // ==================================================

    useEffect(() => {

        // ----------------------------------------------
        // Modification d'une réponse
        // ----------------------------------------------

        if (reply) {

            setData({
                ...emptyReply,
                ...reply
            });

        }

        // ----------------------------------------------
        // Nouvelle réponse
        // ----------------------------------------------

        else {

            setData({
                ...emptyReply,
                messageObject: topic?.topicObject || ""
            });

        }


        // Réinitialiser les fichiers locaux
        setFiles({
            file1: null,
            file2: null,
            file3: null,
            file4: null
        });

        setErrors({});

    }, [reply, topic]);


    // ==================================================
    // MODIFICATION D'UN CHAMP
    // ==================================================

    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;


        setData(old => ({
            ...old,
            [name]: value
        }));
    };


    // ==================================================
    // SÉLECTION D'UN FICHIER
    // ==================================================

    const handleFileChange = (index, event) => {

        const file = event.target.files?.[0];

        if (!file) {
            return;
        }


        // ----------------------------------------------
        // Validation
        // ----------------------------------------------

        const errorMessage = validateFile(file);


        if (errorMessage) {
            alert(errorMessage);
            event.target.value = "";
            return;
        }


        // ----------------------------------------------
        // file1 / file2 / file3 / file4
        // ----------------------------------------------

        const fileKey = `file${index + 1}`;

        setFiles(old => ({
            ...old,
            [fileKey]: file
        }));


        // Permet de resélectionner le même fichier
        event.target.value = "";
    };


    // ==================================================
    // OUVRIR LE SÉLECTEUR DE FICHIER
    // ==================================================

    const openFileChooser = (index) => {

        const input = fileInputRefs.current[index];
        if (input) {
            input.click();
        }
    };


    // ==================================================
    // SUPPRIMER UN FICHIER
    // ==================================================

    const removeFile = (index) => {
        const fileKey = `file${index + 1}`;
        setFiles(old => ({
            ...old,
            [fileKey]: null
        }));


        // Reset de l'input HTML
        const input = fileInputRefs.current[index];
        if (input) {
            input.value = "";
        }
    };


    // ==================================================
    // OBTENIR LE NOM DU FICHIER
    // ==================================================

    const getFileName = (file) => {
        return file ? file.name : "";
    };


    // ==================================================
    // RÉCUPÉRER LE FICHIER PAR INDEX
    // ==================================================

    const getFileByIndex = (index) => {
        const fileKey = `file${index + 1}`;
        return files[fileKey];
    };


    // ==================================================
    // VALIDATION FORMULAIRE
    // ==================================================

 // ==================================================
// VALIDATION FORMULAIRE
// ==================================================

const validate = () => {

    const err = {};

    // --------------------------------------------------
    // Objet
    // --------------------------------------------------

    if (!data.messageObject) {

        setData(old => ({
            ...old,
            messageObject: topic?.topicObject || ""
        }));
    }


    // --------------------------------------------------
    // Vérifier le message
    // --------------------------------------------------

    const messageBody =
        data.messageBody
            ? data.messageBody.trim()
            : "";


    // --------------------------------------------------
    // Vérifier les fichiers
    // --------------------------------------------------

    const hasAttachment =
        Boolean(files.file1) ||
        Boolean(files.file2) ||
        Boolean(files.file3) ||
        Boolean(files.file4);


  
    if (!messageBody && !hasAttachment) {

        err.messageBody =
            t("forum.ForumReply.messageOrFileRequired")
    }


    setErrors(err);

    return Object.keys(err).length === 0;
};


    // ==================================================
    // UPLOAD D'UN FICHIER
    // ==================================================

    const uploadAttachment = async (
        replyId,
        file,
        fileNumber
    ) => {

        if (!file) {
            return;
        }


        const formData = new FormData();

        formData.append("file", file);


        await uploadFileToForumReply(
            replyId,
            fileNumber,
            formData
        );
    };


    // ==================================================
    // ENREGISTREMENT
    // ==================================================

    const handleSubmit = async (event) => {

        event.preventDefault();


        if (!validate()) {
            return;
        }


        setLoading(true);


        try {

            let response;


            // ------------------------------------------
            // INSERTION
            // ------------------------------------------

            if (!data.id) {

                response =
                    await postForumReply(data);
            }


            // ------------------------------------------
            // MODIFICATION
            // ------------------------------------------

            else {

                response =
                    await updateForumReply(data);
            }


            // ------------------------------------------
            // RÉPONSE ENREGISTRÉE
            // ------------------------------------------

            const savedReply = response.data;


            // ------------------------------------------
            // UPLOAD FICHIER 1
            // ------------------------------------------

            await uploadAttachment(
                savedReply.id,
                files.file1,
                1
            );


            // ------------------------------------------
            // UPLOAD FICHIER 2
            // ------------------------------------------

            await uploadAttachment(
                savedReply.id,
                files.file2,
                2
            );


            // ------------------------------------------
            // UPLOAD FICHIER 3
            // ------------------------------------------

            await uploadAttachment(
                savedReply.id,
                files.file3,
                3
            );


            // ------------------------------------------
            // UPLOAD FICHIER 4
            // ------------------------------------------

            await uploadAttachment(
                savedReply.id,
                files.file4,
                4
            );


            // ------------------------------------------
            // CALLBACK
            // ------------------------------------------

            if (onSaved) {
                onSaved(savedReply);
            }


            // ------------------------------------------
            // FERMETURE
            // ------------------------------------------

            if (onClose) {
                onClose();
            }

        }
        catch (error) {

            console.error(error);

            console.error(
                "Erreur complète :",
                error
            );


            if (error.response) {

                console.error(
                    "Status :",
                    error.response.status
                );

                console.error(
                    "Body :",
                    error.response.data
                );
            }


            alert(
                "Erreur lors de l'enregistrement."
            );
        }
        finally {

            setLoading(false);
        }
    };


    // ==================================================
    // RÉINITIALISATION
    // ==================================================

    const handleReset = () => {

        setData({
            ...emptyReply,
            messageObject:
                topic?.topicObject || ""
        });


        setFiles({
            file1: null,
            file2: null,
            file3: null,
            file4: null
        });


        setErrors({});


        // Reset inputs HTML
        fileInputRefs.current.forEach(input => {

            if (input) {
                input.value = "";
            }
        });
    };


    // ==================================================
    // RENDER
    // ==================================================

    return (

        <div className="forum-reply-overlay">

            <div className="forum-reply-container">


                {/* ==================================================
                    HEADER
                ================================================== */}

                <div className="forum-reply-header">

                    <h4>

                        <i className="bi bi-chat-left-text-fill me-2"></i>

                        {
                            data.id
                                ? "Modifier une réponse"
                                : t("forum.ForumReply.backToSubject")
                        }

                    </h4>


                    <button
                        type="button"
                        className="btn-close"
                        onClick={onClose}
                        disabled={loading}
                    />

                </div>


                {/* ==================================================
                    FORMULAIRE
                ================================================== */}

                <form
                    onSubmit={handleSubmit}
                    className="forum-reply-form"
                >


                    {/* ==================================================
                        BODY
                    ================================================== */}

                    <div className="forum-reply-body">


                        {/* ==================================================
                            OBJET
                        ================================================== */}

                        <div className="mb-4">

                            <label className="form-label fw-bold">

                                {t("forum.ForumReply.subject")}

                            </label>


                            <input
                                type="text"
                                name="messageObject"
                                className="form-control"
                                value={data.messageObject}
                                disabled
                                readOnly
                            />


                            {
                                errors.messageObject && (

                                    <div className="invalid-feedback">

                                        {errors.messageObject}

                                    </div>
                                )
                            }

                        </div>


                        {/* ==================================================
                            MESSAGE
                        ================================================== */}

                        <div className="mb-4">

                            <label className="form-label fw-bold">

                                {t("forum.ForumReply.reply")}

                            </label>


                            <textarea
                                rows={10}
                                name="messageBody"
                                className={
                                    `form-control ${
                                        errors.messageBody
                                            ? "is-invalid"
                                            : ""
                                    }`
                                }
                                value={data.messageBody}
                                onChange={handleChange}
                                placeholder={
                                    t(
                                        "forum.ForumReply.enterYourReply"
                                    )
                                }
                            />


                            {
                                errors.messageBody && (

                                    <div className="invalid-feedback">

                                        {errors.messageBody}

                                    </div>
                                )
                            }

                        </div>


                        {/* ==================================================
                            PIÈCES JOINTES
                        ================================================== */}

                        <div className="card border-0 shadow-sm">

                            <div className="card-header bg-light">

                                <h5 className="mb-0">

                                    <i className="bi bi-paperclip me-2"></i>

                                    {t("common.attachments")}

                                </h5>

                            </div>


                            <div className="card-body">

                                <div className="row">

                                    {
                                        [0, 1, 2, 3].map(
                                            (index) => {

                                                const file =
                                                    getFileByIndex(index);


                                                return (

                                                    <div
                                                        className="col-md-6 mb-4"
                                                        key={index}
                                                    >


                                                        {/* LABEL */}

                                                        <label className="form-label">

                                                            {
                                                                t(
                                                                    "common.attachment"
                                                                )
                                                            }

                                                            {" "}

                                                            {index + 1}

                                                        </label>


                                                        {/* ==================================================
                                                            INPUT FILE CACHÉ
                                                        ================================================== */}

                                                        <input
                                                            ref={(el) => {

                                                                fileInputRefs.current[index] =
                                                                    el;

                                                            }}
                                                            type="file"
                                                            hidden
                                                            accept={
                                                                ALLOWED_EXTENSIONS.join(
                                                                    ","
                                                                )
                                                            }
                                                            name={
                                                                `attachedFile${
                                                                    index + 1
                                                                }`
                                                            }
                                                            onChange={(event) =>
                                                                handleFileChange(
                                                                    index,
                                                                    event
                                                                )
                                                            }
                                                        />


                                                        {/* ==================================================
                                                            BOUTON + NOM FICHIER
                                                        ================================================== */}

                                                        <div className="input-group">


                                                            {/* BOUTON CHOISIR */}

                                                            <button
                                                                type="button"
                                                                className="btn btn-success"
                                                                onClick={() =>
                                                                    openFileChooser(
                                                                        index
                                                                    )
                                                                }
                                                                disabled={loading}
                                                            >

                                                                <i className="bi bi-paperclip me-2"></i>

                                                                {
                                                                    t(
                                                                        "common.chooseFile"
                                                                    )
                                                                }

                                                            </button>


                                                            {/* NOM DU FICHIER */}

                                                            <span className="form-control text-start">

                                                                {
                                                                    file ? (

                                                                        <>

                                                                            <i className="bi bi-file-earmark text-success me-2"></i>

                                                                            {
                                                                                getFileName(
                                                                                    file
                                                                                )
                                                                            }

                                                                        </>

                                                                    ) : (

                                                                        <span className="text-muted">

                                                                            {
                                                                                t(
                                                                                    "common.noAttachment"
                                                                                )
                                                                            }

                                                                        </span>
                                                                    )
                                                                }

                                                            </span>


                                                            {/* ==================================================
                                                                SUPPRIMER
                                                            ================================================== */}

                                                            {
                                                                file && (

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger"
                                                                        onClick={() =>
                                                                            removeFile(
                                                                                index
                                                                            )
                                                                        }
                                                                        disabled={loading}
                                                                        title={
                                                                            t(
                                                                                "button.delete"
                                                                            )
                                                                        }
                                                                    >

                                                                        <i className="bi bi-x-circle"></i>

                                                                    </button>

                                                                )
                                                            }

                                                        </div>

                                                    </div>

                                                );

                                            }
                                        )
                                    }

                                </div>

                            </div>

                        </div>


                        {/* ==================================================
                            INFORMATION
                        ================================================== */}

                        <div className="alert alert-light border mt-4">

                            <i className="bi bi-info-circle-fill text-success me-2"></i>

                            {t("forum.ForumReply.allowedFormats")}

                            {" "}

                            <strong>

                                {t(
                                    "forum.ForumReply.allowedFileTypes"
                                )}

                            </strong>

                            <br />

                            {t(
                                "forum.ForumReply.maximumRecommendedSize"
                            )}

                            {" "}

                            <strong>

                                {t(
                                    "forum.ForumReply.megabytes"
                                )}

                            </strong>

                            {" "}

                            {t(
                                "forum.ForumReply.perFile"
                            )}

                        </div>


                    </div>


                    {/* ==================================================
                        FOOTER
                    ================================================== */}

                    <div className="forum-reply-footer">


                        {/* RESET */}

                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleReset}
                            disabled={loading}
                        >

                            {t(
                                "forum.ForumReply.reset"
                            )}

                        </button>


                        {/* ANNULER */}

                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={onClose}
                            disabled={loading}
                        >

                            {t("button.cancel")}

                        </button>


                        {/* ENREGISTRER */}

                        <button
                            type="submit"
                            className="btn btn-success"
                            disabled={loading}
                        >

                            {
                                loading
                                    ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />

                                            Enregistrement...
                                        </>
                                    )
                                    : t(
                                        "forum.ForumReply.publishReply"
                                    )
                            }

                        </button>

                    </div>


                </form>

            </div>

        </div>
    );
}
